import { makeAutoObservable } from 'mobx'
import { isLink } from 'shared/lib'
import { type IResponseMediaShort } from 'entities/Attachment'
import { Attachment } from 'entities/Attachment/model/Attachment'
import { type IResponseBroadcastById } from 'entities/Broadcast/api/types'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import {
  type ISMSMessageFieldStoreProps,
  SMSMessageFieldStore,
} from 'widgets/MessageFormFields/SMSMessageField/store/SMSMessageFieldStore'

export class BroadcastSMSStore {
  smsMessageFieldStore: SMSMessageFieldStore
  constructor(props: ISMSMessageFieldStoreProps, private _entities: IEntitiesStore) {
    this.smsMessageFieldStore = new SMSMessageFieldStore(props, this._entities)
    makeAutoObservable(this)
  }

  init = async (response: IResponseBroadcastById) => {
    const messageText = response.message || ''

    const allUrlsInMessage = this.extractUrlsFromMessage(messageText)

    const mediaUrlShortUrls = this.getMediaUrlShortUrls(response.media_url || [])

    // Find URLs that need to be checked via API (not in media_url)
    // These could be old attachments or regular links
    const urlsToCheckForAttachments = allUrlsInMessage.filter((url) => !mediaUrlShortUrls.has(url))

    // Request information about potential attachments
    if (urlsToCheckForAttachments.length > 0) {
      this._entities.attachmentStore.checkPreviousAttachment(urlsToCheckForAttachments)
    }

    // Create attachments from media_url (images, videos, documents, audio)
    let allAttachments = this.createAttachmentsFromMediaUrl(response.media_url || [])

    // Wait for API response and create attachments from previousAttachmentMap
    // Only create attachments for URLs where valid attachment data was found
    if (urlsToCheckForAttachments.length > 0) {
      const additionalAttachments = await this.createAttachmentsFromPreviousMap(
        urlsToCheckForAttachments
      )
      allAttachments = [...allAttachments, ...additionalAttachments]
    }

    const updatedMessageText = this.replaceOutdatedUrls(messageText, allUrlsInMessage, response)

    this.messageFieldStore.setMessageData({
      attachments: allAttachments,
      message: updatedMessageText,
      isFocus: false,
      isReset: false,
      isReplace: true,
    })
  }

  private extractUrlsFromMessage(messageText: string): string[] {
    const allUrlsInMessage: string[] = []
    messageText.split(/\n/).forEach((text) =>
      text.split(' ').forEach((word) => {
        if (isLink(word)) {
          allUrlsInMessage.push(word)
        }
      })
    )
    return allUrlsInMessage
  }

  private getMediaUrlShortUrls(mediaUrl: IResponseMediaShort[]): Set<string> {
    return new Set(
      mediaUrl.map((media) => media.source_short).filter((url): url is string => Boolean(url))
    )
  }

  private createAttachmentsFromMediaUrl(mediaUrl: IResponseMediaShort[]): Attachment[] {
    if (!mediaUrl.length) return []

    const usedShortUrls = new Set<string>()

    return mediaUrl.map((media) => {
      // Check if this is a GIF preview for a video
      const isPreviewGif = !!(
        media.content_type === 'image/gif' &&
        media.source_short &&
        media.original_source &&
        media.original_source !== media.source
      )

      const attachment = new Attachment({
        responseMediaShort: {
          ...media,
          is_allowed_for_media_url: isPreviewGif ? true : media.is_allowed_for_media_url,
        },
      })

      if (media.source_short && !usedShortUrls.has(media.source_short)) {
        attachment.source_short = media.source_short
        usedShortUrls.add(media.source_short)
      }

      return attachment
    })
  }

  private async createAttachmentsFromPreviousMap(urls: string[]): Promise<Attachment[]> {
    await new Promise((resolve) => setTimeout(resolve, 300))

    const additionalAttachments: Attachment[] = []
    const stillWaitingUrls: string[] = []

    // First attempt: check which URLs have responses
    urls.forEach((url) => {
      const previousAttachment = this._entities.attachmentStore.getAttachmentByLink(url)

      if (previousAttachment === undefined || previousAttachment === null) {
        stillWaitingUrls.push(url)
        return
      }

      // Only create attachment if valid attachment data was found
      if (this.isValidAttachment(previousAttachment)) {
        const attachment = new Attachment({
          responseMediaShort: {
            content_type: previousAttachment.content_type,
            source: previousAttachment.source,
            name: previousAttachment.name,
            url: previousAttachment.source,
            source_short: url,
            size: previousAttachment.size?.toString(),
            original_source: previousAttachment.original_source,
            is_allowed_for_media_url: this.determineIsAllowedForMediaUrl(
              previousAttachment.content_type
            ),
          },
        })

        additionalAttachments.push(attachment)
      }
    })

    if (stillWaitingUrls.length > 0) {
      await new Promise((resolve) => setTimeout(resolve, 500))

      stillWaitingUrls.forEach((url) => {
        const previousAttachment = this._entities.attachmentStore.getAttachmentByLink(url)

        if (previousAttachment && this.isValidAttachment(previousAttachment)) {
          const attachment = new Attachment({
            responseMediaShort: {
              content_type: previousAttachment.content_type,
              source: previousAttachment.source,
              name: previousAttachment.name,
              url: previousAttachment.source,
              source_short: url,
              size: previousAttachment.size?.toString(),
              original_source: previousAttachment.original_source,
              is_allowed_for_media_url: this.determineIsAllowedForMediaUrl(
                previousAttachment.content_type
              ),
            },
          })

          additionalAttachments.push(attachment)
        }
      })
    }

    return additionalAttachments
  }

  private isValidAttachment(attachment: Attachment | null): attachment is Attachment {
    if (!attachment) return false

    const contentType = attachment.content_type
    const invalidTypes = ['text/html', 'application/xhtml+xml']

    if (invalidTypes.includes(contentType)) {
      return false
    }

    if (!attachment.source) {
      return false
    }

    return true
  }

  private determineIsAllowedForMediaUrl(contentType: string): boolean {
    // Types that are NOT rendered as MMS (need text preview)
    const notAllowedTypes = [
      // Documents
      'application/pdf',
      'text/csv',
      'text/plain',
      'text/markdown',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      // Audio
      'audio/mpeg',
      'audio/mp3',
      'audio/wav',
      'audio/ogg',
      // Videos
      'video/mp4',
      'video/quicktime',
      'video/x-msvideo',
    ]

    if (notAllowedTypes.includes(contentType)) {
      return false
    }

    if (contentType.startsWith('video/') || contentType.startsWith('audio/')) {
      return false
    }

    // Images and GIFs are displayed as MMS
    if (contentType.startsWith('image/')) {
      return true
    }

    return false
  }

  private replaceOutdatedUrls(
    messageText: string,
    allUrlsInMessage: string[],
    response: IResponseBroadcastById
  ): string {
    if (!response.media_url || allUrlsInMessage.length === 0) {
      return messageText
    }

    const responseShortUrls =
      response.short_urls
        ?.map((item) => item.short_url)
        .filter((url): url is string => Boolean(url)) || []

    const mediaUrlShortUrls =
      response.media_url
        ?.map((media) => media.source_short)
        .filter((url): url is string => Boolean(url)) || []

    const allKnownShortUrls = [...new Set([...responseShortUrls, ...mediaUrlShortUrls])]

    const shortenDomains = this._entities.shortLinkStore.shortenDomains || []
    const isShortUrl = (url: string): boolean => {
      try {
        const urlObj = new URL(url)
        return shortenDomains.some((domain) => urlObj.hostname.includes(domain))
      } catch {
        return false
      }
    }

    const unmatchedUrlsInMessage = allUrlsInMessage.filter(
      (url) => !allKnownShortUrls.includes(url) && isShortUrl(url)
    )

    const unmatchedUrlsInPayload = responseShortUrls.filter(
      (url) => !allUrlsInMessage.includes(url)
    )

    if (unmatchedUrlsInMessage.length > 0 && unmatchedUrlsInPayload.length > 0) {
      const urlUpdateMap = new Map<string, string>()

      if (unmatchedUrlsInMessage.length === unmatchedUrlsInPayload.length) {
        unmatchedUrlsInMessage.forEach((oldUrl, index) => {
          if (index < unmatchedUrlsInPayload.length) {
            urlUpdateMap.set(oldUrl, unmatchedUrlsInPayload[index])
          }
        })
      } else if (unmatchedUrlsInPayload.length >= unmatchedUrlsInMessage.length) {
        unmatchedUrlsInMessage.forEach((oldUrl, index) => {
          if (index < unmatchedUrlsInPayload.length) {
            urlUpdateMap.set(oldUrl, unmatchedUrlsInPayload[index])
          }
        })
      }

      if (urlUpdateMap.size > 0) {
        let updatedMessageText = messageText
        urlUpdateMap.forEach((newUrl, oldUrl) => {
          updatedMessageText = updatedMessageText.replace(oldUrl, newUrl)
        })
        return updatedMessageText
      }
    }

    return messageText
  }

  get messageDataParams() {
    return this.smsMessageFieldStore.messageDataParams
  }

  get messageFieldStore() {
    return this.smsMessageFieldStore.messageFieldStore
  }

  get isError() {
    return this.smsMessageFieldStore.isError
  }

  get segments_count_rcs() {
    return this.smsMessageFieldStore.segments_count_rcs
  }

  get segments_count() {
    return this.smsMessageFieldStore.segments_count
  }
}
